// The inbox, notification settings, mutes, direct group invites and the group log
// (apps/edge/src/inbox.ts, groups.ts, groupEvents.ts), one function per route like friendsApi.ts.
import {
  GroupEventList,
  Inbox,
  InboxSummary,
  InviteToGroupResponse,
  JoinGroupResponse,
  MuteList,
  NotificationPrefs,
  OkResponse,
  UpdateNotificationPrefsRequest,
  type MuteScope,
} from "@proef/contract";
import type { z } from "zod";
import { call } from "../edgeApi";

function post(body: unknown = {}): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}

const id = encodeURIComponent;

// `shop=1` says this bundle can read the update a group's wine list tick earns; an older bundle
// is never sent it, and so never has it in its badge either.
export function getInbox(): Promise<Inbox> {
  return call(Inbox, "/v1/inbox?shop=1");
}

export function getInboxSummary(): Promise<InboxSummary> {
  return call(InboxSummary, "/v1/inbox/summary?shop=1");
}

/** Marks the updates seen up to `before`, so one arriving meanwhile stays new. */
export function markInboxRead(before: string): Promise<InboxSummary> {
  return call(InboxSummary, "/v1/inbox/read?shop=1", post({ before }));
}

export function getNotificationPrefs(): Promise<NotificationPrefs> {
  return call(NotificationPrefs, "/v1/notifications/prefs");
}

export function updateNotificationPrefs(
  change: z.input<typeof UpdateNotificationPrefsRequest>,
): Promise<NotificationPrefs> {
  return call(NotificationPrefs, "/v1/notifications/prefs", {
    method: "PUT",
    body: JSON.stringify(UpdateNotificationPrefsRequest.parse(change)),
  });
}

export function listMutes(): Promise<MuteList> {
  return call(MuteList, "/v1/mutes");
}

export function mute(scope: MuteScope, subjectId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/mutes/${scope}/${id(subjectId)}`, { method: "PUT" });
}

export function unmute(scope: MuteScope, subjectId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/mutes/${scope}/${id(subjectId)}`, { method: "DELETE" });
}

/** Invites accepted friends; each joins only after accepting in their own inbox. */
export function inviteFriendsToGroup(
  groupId: string,
  friendIds: readonly string[],
): Promise<InviteToGroupResponse> {
  return call(InviteToGroupResponse, `/v1/groups/${id(groupId)}/invites`, post({ friendIds }));
}

function groupInvite(groupId: string, userId: string, action: string): string {
  return `/v1/groups/${id(groupId)}/invites/${id(userId)}/${action}`;
}

export function acceptGroupInvite(groupId: string, userId: string): Promise<JoinGroupResponse> {
  return call(JoinGroupResponse, groupInvite(groupId, userId, "accept"), post());
}

export function declineGroupInvite(groupId: string, userId: string): Promise<OkResponse> {
  return call(OkResponse, groupInvite(groupId, userId, "decline"), post());
}

export function cancelGroupInvite(groupId: string, userId: string): Promise<OkResponse> {
  return call(OkResponse, groupInvite(groupId, userId, "cancel"), post());
}

export function remindGroupInvite(groupId: string, userId: string): Promise<OkResponse> {
  return call(OkResponse, groupInvite(groupId, userId, "remind"), post());
}

export function declineSessionInvite(sessionId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/sessions/${id(sessionId)}/decline`, post());
}

export function listGroupEvents(groupId: string, cursor?: string): Promise<GroupEventList> {
  // `sessions=1` says this bundle can read the session lines; an older one is never sent them.
  const query = cursor === undefined ? "?sessions=1" : `?sessions=1&cursor=${id(cursor)}`;
  return call(GroupEventList, `/v1/groups/${id(groupId)}/events${query}`);
}
